import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';

import { DRIVER_LOGGED_IN_QUALIFIER } from './diagnostics';
import { LogEntry } from './types';
import { kafkaMessagesQuery } from '../components/bigquery/common/graphql';
import { BigQueryMessagesQuery, BigQueryMessagesQueryVariables } from '../lib/graphql';

const DAY_MS = 24 * 3600 * 1000;
const LOOKBACK_DAYS = 3;
const DEFAULT_LOOKBACK_DAYS = 30;
const MAX_WINDOW_DAYS = 30;

const ALLOCATION_CHANGED = 'tour.TourVehicleAllocationChanged';
const TOUR_EVENT_INPUT = 'tour_events_input.TourEventInput';

const MESSAGE_TYPES = [ALLOCATION_CHANGED, TOUR_EVENT_INPUT];
const TOPICS = ['tour', 'tour-events-input'];

const SAFE_FIELDS: Record<string, string[]> = {
  [ALLOCATION_CHANGED]: ['allocation_device_type', 'allocation_source', 'allocated_at', 'clear_allocation'],
  [TOUR_EVENT_INPUT]: ['source', 'external_qualifier', 'sixfold_qualifier', 'external_event_source'],
};

const MESSAGE_NAME: Record<string, string> = {
  [ALLOCATION_CHANGED]: 'tour.TourVehicleAllocationChanged',
  [TOUR_EVENT_INPUT]: 'tour_events_input.TourEventInput',
};

const parseMessage = (raw: string): Record<string, unknown> => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? Object.fromEntries(Object.entries(parsed)) : {};
  } catch {
    return {};
  }
};

const describe = (messageType: string, payload: Record<string, unknown>) =>
  (SAFE_FIELDS[messageType] ?? [])
    .filter((field) => payload[field] !== undefined && payload[field] !== null)
    .map((field) => `${field}=${String(payload[field])}`)
    .join(', ');

export const buildKafkaLogs = (messages: BigQueryMessagesQuery['bigQueryMessages']): LogEntry[] =>
  messages.flatMap(({ produced_at, message, message_type }, index): LogEntry[] => {
    const payload = parseMessage(message);
    if (message_type === TOUR_EVENT_INPUT && payload.external_qualifier === DRIVER_LOGGED_IN_QUALIFIER) {
      return [];
    }
    return [
      {
        id: `kafka:${message_type}:${produced_at}:${index}`,
        timestamp: produced_at,
        source: 'Kafka',
        name: MESSAGE_NAME[message_type] ?? message_type,
        details: describe(message_type, payload),
        significant: false,
        severity: 'info',
      },
    ];
  });

export const useKafkaLogs = (tourId: number, anchorIso: string | null) => {
  const [now] = useState(() => Date.now());
  const variables = useMemo<BigQueryMessagesQueryVariables>(() => {
    const anchor = anchorIso === null ? now - DEFAULT_LOOKBACK_DAYS * DAY_MS : new Date(anchorIso).getTime();
    const from = Math.max(Math.min(anchor - LOOKBACK_DAYS * DAY_MS, now - DAY_MS), now - MAX_WINDOW_DAYS * DAY_MS);
    return {
      keys: [String(tourId)],
      messageTypes: MESSAGE_TYPES,
      topics: TOPICS,
      from: new Date(from).toISOString(),
      to: new Date(now).toISOString(),
    };
  }, [tourId, anchorIso, now]);

  const { data, loading, error } = useQuery<BigQueryMessagesQuery, BigQueryMessagesQueryVariables>(kafkaMessagesQuery, {
    variables,
  });
  const logs = useMemo(() => buildKafkaLogs(data?.bigQueryMessages ?? []), [data]);
  return { logs, loading, error };
};
